import { useEffect, useState } from "react";

import FilterV2View from "./FilterV2View/FilterV2View";
import { useFilter } from "../../hook";
import { AggregateWindow, NB_POINTS_MAX } from "../../types/types";
import { calculateDifferenceDate } from "../../../../common/utils/time.utils";

export type Preset = "today" | "this-month" | "this-year";

export default function FilterV2Presenter() {
  const { filter, setFilter, handleNextWindow, handlePrevWindow } = useFilter();

  const presetDatevalue : {
    [key in Preset]: { startDate: string, stopDate: string, aggregateWindow: AggregateWindow }
  } = {
    "today": {
      startDate: getTodayDate({ resetHour: true }),
      stopDate: getTodayDate({ resetHour: true, nbDay: 1 }),
      aggregateWindow: "1h",
    },
    "this-month": {
      startDate: getTodayDate({ resetDay: true, resetHour: true }),
      stopDate: getTodayDate({ resetDay: true, resetHour: true, nbMonth: 1 }),
      aggregateWindow: "1d",
    },
    "this-year": {
      startDate: getTodayDate({ nbMonth: 0, resetMonth: true }),
      stopDate: getTodayDate({ nbMonth: +11, resetMonth: true }),
      aggregateWindow: "1mo",
    },
    }
  
  const [startDate, setStartDate] = useState<Date | null>();
  const [stopDate, setStopDate] = useState<Date | null>();
  const [aggregateWindow, setAggregateWindow] =
    useState<AggregateWindow | null>("1d");
  const [isValidForm, setIsValidForm] = useState<boolean>(true);
  const [disabledAggregationList, setDisabledAggregationList] = useState<
    AggregateWindow[]
  >([]);


  const isValid = () => {
    const dateStartMili = startDate?.getTime() || 0;
    const dateStopMili = stopDate?.getTime() || 0;
    if (dateStartMili > dateStopMili) {
      return false;
    }
    return true;
  };

  useEffect(() => {
    setIsValidForm(isValid());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, stopDate, aggregateWindow]);
  
  const submitFilter = () => {
    if (!startDate || !stopDate) {
      return;
    }
    const startDateString = startDate?.toISOString();
    const stopDateString = stopDate?.toISOString();

    setFilter({
      ...filter,
      startDate: startDateString,
      stopDate: stopDateString,
      aggregateWindow,
    });
  };

  useEffect(() => {
    setDisabledAggregationList([]);
    if (filter.startDate && filter.stopDate) {
      const startDateObj = new Date(filter.startDate);
      const stopDateObj = new Date(filter.stopDate);
      
      // Vérifier que les dates sont valides
      if (isNaN(startDateObj.getTime()) || isNaN(stopDateObj.getTime())) {
        console.warn('Invalid dates in FilterV2Presenter:', { startDate: filter.startDate, stopDate: filter.stopDate });
        return;
      }
      
      setStartDate(startDateObj);
      setStopDate(stopDateObj);
      

      const nbMinutes = calculateDifferenceDate(
        startDateObj,
        stopDateObj,
        "m"
      );
      const nbHours = calculateDifferenceDate(
        startDateObj,
        stopDateObj,
        "h"
      );
      const nbDays = calculateDifferenceDate(
        startDateObj,
        stopDateObj,
        "d"
      );
      const nbMonth = calculateDifferenceDate(
        startDateObj,
        stopDateObj,
        "mo"
      );

      const nb15MinIntervals = nbMinutes / 15;
      const disabledList: AggregateWindow[] = [];

      // Désactiver "15m" si trop de points ou période trop longue (plus d'un jour)
      if (nb15MinIntervals > NB_POINTS_MAX || nbDays > 1) {
        disabledList.push("15m");
      }

      if (nbHours > NB_POINTS_MAX) {
        disabledList.push("1h");
      }

      if (nbDays > NB_POINTS_MAX) {
        disabledList.push("1d");
      }
      
      if (nbDays <= 1) {
        disabledList.push("1d", "1mo");
      } else if (nbMonth <= 1) {
        disabledList.push("1mo");
      }

      setDisabledAggregationList(disabledList);
      setAggregateWindow(filter.aggregateWindow || null);
    }
  }, [filter.startDate, filter.stopDate, filter.aggregateWindow]);
  
  useEffect(() => {
    setDisabledAggregationList([]);
    if (startDate && stopDate) {

      

      const nbMinutes = calculateDifferenceDate(
        new Date(startDate),
        new Date(stopDate),
        "m"
      );
      const nbHours = calculateDifferenceDate(
        new Date(startDate),
        new Date(stopDate),
        "h"
      );
      const nbDays = calculateDifferenceDate(
        new Date(startDate),
        new Date(stopDate),
        "d"
      );
      const nbMonth = calculateDifferenceDate(
        new Date(startDate),
        new Date(stopDate),
        "mo"
      );

      const nb15MinIntervals = nbMinutes / 15;
      const disabledList: AggregateWindow[] = [];

      // Désactiver "15m" si trop de points ou période trop longue (plus d'un jour)
      if (nb15MinIntervals > NB_POINTS_MAX || nbDays > 1) {
        disabledList.push("15m");
        if (aggregateWindow === "15m") {
          setAggregateWindow("1h");
        }
      }

      if (nbHours > NB_POINTS_MAX) {
        disabledList.push("1h");
        if (aggregateWindow === "1h") {
          setAggregateWindow("1d");
        }
      }

      if (nbDays > NB_POINTS_MAX) {
        disabledList.push("1d");
        if (aggregateWindow === "1d") {
          setAggregateWindow("1mo");
        }
        if (aggregateWindow === "1h") {
          setAggregateWindow("1mo");
        }
      }
      
      if (nbDays <= 1) {
        disabledList.push("1d", "1mo");
        if (aggregateWindow === "1mo") {
          setAggregateWindow("1h");
        }
        if (aggregateWindow === "1d") {
          setAggregateWindow("1h");
        }
      } else if (nbMonth <= 1) {
        disabledList.push("1mo");
        if (aggregateWindow === "1mo") {
          setAggregateWindow("1d");
        }
      }

      setDisabledAggregationList(disabledList);

  
    }
  }, [startDate, stopDate]);// eslint-disable-line react-hooks/exhaustive-deps



  const handleOnClickPreset = (preset: Preset) => {
    if (preset === "today") {
      setFilter({
        ...filter,
        startDate: getTodayDate({ resetHour: true }),
        stopDate: getTodayDate({ resetHour: true, nbDay: 1 }),
        aggregateWindow: "1h",
      });
      return;
    }

    if (preset === "this-month") {
      setFilter({
        ...filter,
        startDate: getTodayDate({ resetDay: true, resetHour: true }),
        stopDate: getTodayDate({ resetDay: true, resetHour: true, nbMonth: 1 }),
        aggregateWindow: "1d",
      });
      return;
    }

    if (preset === "this-year") {
      setFilter({
        ...filter,
        startDate: getTodayDate({ nbMonth: 0, resetMonth: true }),
        stopDate: getTodayDate({ nbMonth: +11, resetMonth: true }),
        aggregateWindow: "1mo",
      });

      return;
    }
  };

  const getActivePreset = () => {
    if (presetDatevalue["this-year"].startDate === filter.startDate

      && presetDatevalue["this-year"].stopDate === filter.stopDate
    ) {
      return "this-year";
    }
    if (presetDatevalue["today"].startDate === filter.startDate

      && presetDatevalue["today"].stopDate === filter.stopDate
    ) {
      return "today";
    }

    if (presetDatevalue["this-month"].startDate === filter.startDate

      && presetDatevalue["this-month"].stopDate === filter.stopDate
    ) {
      return "this-month";
    }

    return undefined;
  }

  return (
    <FilterV2View
      aggregateWindow={aggregateWindow}
      setAggregateWindow={setAggregateWindow}
      onSubmit={submitFilter}
      handleNextWindow={handleNextWindow}
      handlePreviousWindow={handlePrevWindow}
      setDateStart={setStartDate}
      setDateStop={setStopDate}
      dateStart={startDate || undefined}
      dateStop={stopDate || undefined}
      onClickPreset={handleOnClickPreset}
      disabledSubmit={!isValidForm}
      disabledAggregation={disabledAggregationList}
      presetWindowActive={getActivePreset()}
    />
  );
}

export interface DateObject {
  year: number;
  month: number;
  day: number;
}

export interface optionGetTodayDate {
  currentDate?: string;
  nbDay?: number;
  nbMonth?: number;
  nbYear?: number;
  resetHour?: boolean;
  resetMinute?: boolean;
  resetDay?: boolean;
  nbHour?: number;
  resetMonth?: boolean;
}
export const getTodayDate = (option?: optionGetTodayDate) => {
  const date = option?.currentDate ? new Date(option.currentDate) : new Date();

  if (option?.resetHour) {
    date.setUTCHours(0, 0, 0, 0);
  }

  if (option?.resetMinute) {
    date.setUTCMinutes(0, 0, 0);
  }

  if (option?.resetDay) {
    date.setUTCDate(1);
  }

  if (option?.resetMonth) {
    date.setUTCMonth(1);
    date.setUTCDate(1);
    date.setUTCHours(0, 0, 0, 0);
    date.setUTCMinutes(0, 0, 0);
  }

  if (option?.nbHour) {
    date.setUTCHours(date.getUTCHours() + option.nbHour);
  }

  if (option?.nbDay) {
    date.setUTCDate(date.getUTCDate() + option.nbDay);
  }

  if (option?.nbMonth) {
    date.setUTCMonth(date.getUTCMonth() + option.nbMonth);
  }

  if (option?.nbYear) {
    date.setUTCFullYear(date.getUTCFullYear() + option.nbYear);
  }

  return date.toISOString();
};
